import React, { FC, useCallback, useMemo } from 'react';
import KeywordsInput from './KeywordsInput';
import KeywordsArea from './KeywordsArea';
import { IKeyword } from './types';
import styles from './index.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { SearchProfileColor } from 'src/helpers/constants';

export function createNewKeyword(title: string): IKeyword {
  return {
    title
  };
}

function toKeywords(mpKeywords: readonly string[]): IKeyword[] {
  return mpKeywords.map(keyword => createNewKeyword(keyword));
}

interface IProps {
  disabled: boolean;
  label: string;
  keywords: string[];
  onChange: (keywords: string[]) => void;
  color: SearchProfileColor;
  existingKeywords: string[];
}

export const Keywords: FC<IProps> = props => {
  const { disabled, label, onChange, keywords: rawKeywords, color, existingKeywords } = props;

  const keywords = useMemo(() => {
    return toKeywords(rawKeywords);
  }, [rawKeywords]);

  const onUpdateKeywords = useCallback(
    (keywords: readonly IKeyword[]) => {
      onChange(keywords.map(_ => _.title));
    },
    [onChange]
  );

  const addKeyword = useCallback(
    (userInput: string): void => {
      const uniqFromInput = [
        ...new Set(
          userInput
            .split(',')
            .map(_ => _.trim())
            .filter(_ => !!_)
        )
      ];
      const uniqFromKeywords = uniqFromInput.filter(input => !keywords.map(_ => _.title).includes(input));

      if (!!uniqFromKeywords.length && !disabled) {
        const newKeywords = uniqFromKeywords.map(_ => createNewKeyword(_));
        const newKeywordsState: IKeyword[] = [...keywords, ...newKeywords];
        onUpdateKeywords(newKeywordsState);
      }
    },
    [disabled, keywords, onUpdateKeywords]
  );

  const removeKeyword = useCallback(
    (title: string): void => {
      const newKeywordsState: IKeyword[] = keywords.filter(keyword => keyword.title !== title);
      if (!disabled) {
        onUpdateKeywords(newKeywordsState);
      }
    },
    [disabled, keywords, onUpdateKeywords]
  );

  return (
    <div className={styles.keywords}>
      <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
      <ul className={styles.keywordsList}>
        <KeywordsArea keywords={keywords} removeKeyword={removeKeyword} disabled={disabled} color={color} />
        <KeywordsInput addKeyword={addKeyword} disabled={disabled} label={label} existingKeywords={existingKeywords} />
      </ul>
    </div>
  );
};

export default Keywords;
